import { CustomerProductsState } from 'store/slices/customerProductsSlice';
import { CustomerState } from 'store/slices/customerSlice';

/**
 * What a logged-in user may do on a protected step:
 * - 'loading': customer or policies are still being fetched
 * - 'not-customer': the customer was loaded but has no customer number (`customer_id`)
 * - 'no-policies': a customer without ACTIVE/PENDING policies, or the policies call failed
 * - 'has-policies': a customer with at least one ACTIVE/PENDING policy; the step is shown
 * - 'error': the customer API call failed
 */
export type CustomerAccess = 'loading' | 'not-customer' | 'no-policies' | 'has-policies' | 'error';

/**
 * Reads the customer and policies that useFetchCustomer already loads into Redux, so the gate
 * makes no API calls of its own.
 */
export const getCustomerAccess = (
	customer: Pick<CustomerState, 'customer_id' | 'isDataFetched' | 'loading' | 'error'>,
	customerProducts: Pick<CustomerProductsState, 'products' | 'loading' | 'error'>
): CustomerAccess => {
	if (!customer.isDataFetched || customer.loading) {
		return 'loading';
	}
	if (customer.error) {
		return 'error';
	}
	// Someone who isn't a customer gets a successful response without a customer number.
	if (!customer.customer_id) {
		return 'not-customer';
	}
	// A failed policies call counts as "no policies".
	if (customerProducts.error) {
		return 'no-policies';
	}
	// `products` is only set once the policies response has been stored (setCustomerProducts).
	if (customerProducts.loading || !customerProducts.products) {
		return 'loading';
	}
	return customerProducts?.products?.length > 0 ? 'has-policies' : 'no-policies';
};
